// v2 swan — a mute swan in full sail: a long hull with the wing mantles arched
// up over the back (the "busking" posture), a raised pointed tail, and a proper
// four-bend S-neck that rises, straightens and tips the head gracefully forward
// over an orange bill with the black basal knob. Two species variants: v0 the
// white mute swan, v1 a black swan (dark plumage, white flight tips, red bill).
// Ported 1:1 from age-of-mcp's v2/swan.ts, using the library's shared `wetland`
// (eyes) helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const PLUMAGES = [
  { body: "#f6f8fa", mantle: "#e9edf1", wing: "#dde3e9", neck: "#f2f5f7",
    head: "#fbfcfd", bill: "#e07020", knob: "#16140f", tip: "#e9edf1" },
  { body: "#332e2c", mantle: "#403a36", wing: "#2a2624", neck: "#3a3431",
    head: "#443d39", bill: "#c83838", knob: "#2a2422", tip: "#e8e6e2" },
];

function swanParts(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  return [
    // hull on the waterline
    { geometry: new THREE.IcosahedronGeometry(0.052, 0), color: c.body,
      position: [0, 0.03, 0], scale: [1.75, 0.9, 1.0] },
    // arched wing mantles raised over the back, plus the puffed rear arch
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.mantle,
      position: [-0.018, 0.054, 0.024], scale: [1.35, 0.8, 0.55],
      rotation: [0, 0, -0.1] },
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.mantle,
      position: [-0.018, 0.054, -0.024], scale: [1.35, 0.8, 0.55],
      rotation: [0, 0, -0.1] },
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.wing,
      position: [-0.042, 0.062, 0], scale: [1.1, 0.7, 0.95] },
    // white flight-feather tips peeking at the rear (pop on the black swan)
    { geometry: new THREE.ConeGeometry(0.011, 0.03, 4), color: c.tip,
      position: [-0.072, 0.06, 0.014], rotation: [0, 0, Math.PI / 2 - 0.45],
      scale: [1, 1, 0.5] },
    { geometry: new THREE.ConeGeometry(0.011, 0.03, 4), color: c.tip,
      position: [-0.072, 0.06, -0.014], rotation: [0, 0, Math.PI / 2 - 0.45],
      scale: [1, 1, 0.5] },
    // raised pointed tail
    { geometry: new THREE.ConeGeometry(0.013, 0.036, 4), color: c.body,
      position: [-0.086, 0.046, 0], rotation: [0, 0, Math.PI / 2 - 0.5],
      scale: [1, 1, 0.6] },
    // S-neck — three tapering bends: forward-up, vertical, tipping back
    { geometry: new THREE.CylinderGeometry(0.013, 0.016, 0.045, 5), color: c.neck,
      position: [0.07, 0.05, 0], rotation: [0, 0, -0.5] },
    { geometry: new THREE.CylinderGeometry(0.011, 0.013, 0.045, 5), color: c.neck,
      position: [0.092, 0.085, 0], rotation: [0, 0, -0.1] },
    { geometry: new THREE.CylinderGeometry(0.0095, 0.011, 0.04, 5), color: c.neck,
      position: [0.094, 0.118, 0], rotation: [0, 0, 0.35] },
    // head carried forward-down over the water
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.head,
      position: [0.088, 0.142, 0], scale: [1.3, 0.95, 0.9] },
    // bill angled down with the basal knob above it
    { geometry: new THREE.ConeGeometry(0.0065, 0.024, 4), color: c.bill,
      position: [0.106, 0.136, 0], rotation: [0, 0, -Math.PI / 2 - 0.3],
      scale: [0.7, 1, 1.1] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: c.knob,
      position: [0.098, 0.147, 0] },
    ...wetland.eyes(0.094, 0.148, 0.0105, 0.003),
  ];
}

export const swanV2 = defineModel({
  id: "swan-v2",
  scale: 2.5, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Swan (v2)",
  tags: ["creature","swan","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly swan.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.1,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => swanParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => swanParts(1) },
  ],
  animations: [],
});
